import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { rentalService } from '../../services/rentalService';
import { refillService } from '../../services/refillService';
import stockMovementService from '../../services/stockMovementService';
import { Link } from 'react-router-dom';
import { StatCard } from '../dashboard/StatCard';
import { RefillScheduleModal } from './RefillScheduleModal';
import { VisitScheduleModal } from './VisitScheduleModal';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bgBase: '#faf7ee',
  bgCard: '#ffffff',
  bgElevated: '#f4efe2',
  gold: '#d97706',
  goldLight: '#d97706',
  goldHover: '#b45309',
  textPrimary: '#1c2033',
  textSecondary: '#5b5a52',
  textMuted: '#6b6a5c',
  borderNormal: 'rgba(92,78,51,0.15)',
  borderSubtle: 'rgba(92,78,51,0.25)',
  fontHeading: "'Inter', sans-serif",
  fontLabel: "'Inter', sans-serif",
  fontBody: "'Inter', sans-serif",
};

const thStyle: React.CSSProperties = {
  fontFamily: C.fontLabel,
  fontSize: 9,
  letterSpacing: '0.22em',
  color: C.textMuted,
  textTransform: 'uppercase',
  padding: '10px 14px',
  background: C.bgElevated,
  borderBottom: `1px solid ${C.borderSubtle}`,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  fontFamily: C.fontBody,
  fontSize: 12,
  color: C.textSecondary,
  padding: '10px 14px',
  borderBottom: `1px solid ${C.borderNormal}`,
  whiteSpace: 'nowrap',
};

const cardStyle: React.CSSProperties = {
  background: C.bgCard,
  border: `1px solid ${C.borderNormal}`,
  borderRadius: 6,
};

const sectionHeadingStyle: React.CSSProperties = {
  fontFamily: C.fontHeading,
  fontSize: 20,
  fontWeight: 300,
  color: C.textPrimary,
  letterSpacing: '0.04em',
  margin: 0,
};

const statusBadge = (isOverdue: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  borderRadius: 20,
  fontSize: 10,
  fontFamily: C.fontLabel,
  letterSpacing: '0.14em',
  fontWeight: 600,
  textTransform: 'uppercase',
  background: isOverdue ? 'rgba(201,90,90,0.15)' : 'rgba(92,78,51,0.22)',
  color: isOverdue ? '#e87a7a' : '#fbbf24',
  border: `1px solid ${isOverdue ? 'rgba(201,90,90,0.3)' : 'rgba(217,119,6,0.25)'}`,
});

const contractStatusBadge = (): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  borderRadius: 20,
  fontSize: 10,
  fontFamily: C.fontLabel,
  letterSpacing: '0.14em',
  fontWeight: 600,
  textTransform: 'uppercase',
  background: 'rgba(201,140,90,0.15)',
  color: '#e8b07a',
  border: '1px solid rgba(201,140,90,0.3)',
});

const rentedBadge = (): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  borderRadius: 20,
  fontSize: 10,
  fontFamily: C.fontLabel,
  letterSpacing: '0.14em',
  fontWeight: 600,
  textTransform: 'uppercase',
  background: 'rgba(90,201,140,0.15)',
  color: '#5ac98c',
  border: '1px solid rgba(90,201,140,0.3)',
});

const mlBadge = (): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  borderRadius: 20,
  fontSize: 10,
  fontFamily: C.fontLabel,
  letterSpacing: '0.14em',
  fontWeight: 600,
  background: 'rgba(90,140,201,0.15)',
  color: '#7ab0e8',
  border: '1px solid rgba(90,140,201,0.3)',
});

const conditionBadge = (condition: string): React.CSSProperties => {
  const map: Record<string, { bg: string; color: string; border: string }> = {
    NEW:       { bg: 'rgba(90,201,140,0.15)',  color: '#5ac98c', border: 'rgba(90,201,140,0.3)'  },
    USED:      { bg: 'rgba(90,140,201,0.15)',  color: '#7ab0e8', border: 'rgba(90,140,201,0.3)'  },
    default:   { bg: 'rgba(92,78,51,0.22)',  color: '#fbbf24', border: 'rgba(217,119,6,0.25)'  },
  };
  const s = map[condition] || map.default;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 10px',
    borderRadius: 20,
    fontSize: 10,
    fontFamily: C.fontLabel,
    letterSpacing: '0.14em',
    fontWeight: 600,
    textTransform: 'uppercase',
    background: s.bg,
    color: s.color,
    border: `1px solid ${s.border}`,
  };
};

export const RentalDashboard: React.FC = () => {
  const [showAvailableMachinesModal, setShowAvailableMachinesModal] = useState(false);
  const [showOilUsageModal, setShowOilUsageModal] = useState(false);
  const [showRefillScheduleModal, setShowRefillScheduleModal] = useState(false);
  const [showVisitScheduleModal, setShowVisitScheduleModal] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['rental-dashboard-stats'],
    queryFn: async () => {
      const response = await rentalService.getDashboardStats();
      return response.data;
    }
  });

  const { data: visitsThisWeek, isLoading: visitsLoading } = useQuery({
    queryKey: ['visits-this-week-count'],
    queryFn: async () => {
      const response = await rentalService.getVisitsThisWeekCount();
      return response.data.count;
    }
  });

  const { data: availableMachines, isLoading: availableMachinesLoading } = useQuery({
    queryKey: ['available-serialized-machines'],
    queryFn: async () => {
      return await stockMovementService.getAllAvailableMachines();
    },
    enabled: showAvailableMachinesModal
  });

  const { data: oilUsageData, isLoading: oilUsageLoading } = useQuery({
    queryKey: ['oil-usage-this-month'],
    queryFn: async () => {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
      const response = await refillService.getRefillVisits({
        startDate: startOfMonth.toISOString(),
        endDate: endOfMonth.toISOString()
      });
      return response.data;
    },
    enabled: showOilUsageModal
  });

  const { data: machinesNeedingRefill, isLoading: refillLoading } = useQuery({
    queryKey: ['machines-needing-refill'],
    queryFn: async () => {
      const response = await refillService.getMachinesNeedingRefill({ daysAhead: 7 });
      return response.data;
    }
  });

  const { data: expiringContracts, isLoading: expiringLoading } = useQuery({
    queryKey: ['expiring-contracts'],
    queryFn: async () => {
      const response = await rentalService.getExpiringContracts(30);
      return response.data;
    }
  });

  const { data: rentedMachines, isLoading: rentedLoading } = useQuery({
    queryKey: ['rented-machines'],
    queryFn: async () => {
      const response = await rentalService.getRentedMachines();
      return response.data;
    }
  });

  if (statsLoading) {
    return (
      <div style={{ padding: '32px 16px', background: C.bgBase, minHeight: '100vh' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <div style={{ height: 28, background: C.bgElevated, borderRadius: 4, width: '25%', marginBottom: 28, opacity: 0.6 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} style={{ ...cardStyle, padding: 24 }}>
                <div style={{ height: 14, background: C.bgElevated, borderRadius: 4, width: '75%', marginBottom: 10, opacity: 0.6 }} />
                <div style={{ height: 22, background: C.bgElevated, borderRadius: 4, width: '50%', opacity: 0.6 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const modalOverlay: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(7,7,15,0.85)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  };

  const modalBox: React.CSSProperties = {
    background: C.bgCard,
    border: `1px solid ${C.borderSubtle}`,
    borderRadius: 8,
    maxWidth: 1100,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  const modalHeader: React.CSSProperties = {
    padding: '24px 28px',
    borderBottom: `1px solid ${C.borderSubtle}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  };

  const modalBody: React.CSSProperties = {
    padding: '24px 28px',
    overflowY: 'auto',
    flex: 1,
  };

  const modalFooter: React.CSSProperties = {
    padding: '16px 28px',
    borderTop: `1px solid ${C.borderSubtle}`,
    display: 'flex',
    justifyContent: 'flex-end',
  };

  const closeBtn: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: C.textMuted,
    cursor: 'pointer',
    fontSize: 22,
    lineHeight: 1,
    padding: 0,
  };

  const primaryBtn = (id: string): React.CSSProperties => ({
    fontFamily: C.fontLabel,
    fontSize: 9,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    padding: '9px 22px',
    borderRadius: 4,
    cursor: 'pointer',
    border: 'none',
    background: hoveredBtn === id
      ? `linear-gradient(135deg, ${C.goldHover}, ${C.gold})`
      : `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
    color: C.bgCard,
    transition: 'all 0.2s',
  });

  const goldLinkStyle: React.CSSProperties = {
    fontFamily: C.fontLabel,
    fontSize: 9,
    letterSpacing: '0.14em',
    color: C.gold,
    textDecoration: 'none',
    textTransform: 'uppercase',
  };

  // Quick action card hover helper
  const quickCardStyle = (id: string): React.CSSProperties => ({
    ...cardStyle,
    padding: 24,
    textDecoration: 'none',
    display: 'block',
    transition: 'border-color 0.2s, background 0.2s',
    borderColor: hoveredCard === id ? C.borderSubtle : C.borderNormal,
    background: hoveredCard === id ? C.bgElevated : C.bgCard,
    cursor: 'pointer',
  });

  const statsCardClickable = (id: string): React.CSSProperties => ({
    display: 'block',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    opacity: hoveredCard === id ? 0.8 : 1,
  });

  return (
    <div style={{ padding: '32px 16px', background: C.bgBase, minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        {/* ── Page Header ──────────────────────────────────────────────────────── */}
        <div style={{ marginBottom: 32, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{
              fontFamily: C.fontHeading,
              fontSize: 34,
              fontWeight: 300,
              color: C.textPrimary,
              letterSpacing: '0.04em',
              margin: 0,
              lineHeight: 1.1,
            }}>
              Rental Management Dashboard
            </h1>
            <div style={{ width: 56, height: 1, background: `linear-gradient(to right, ${C.gold}, transparent)`, marginTop: 8 }} />
          </div>
          <Link
            to="/rentals/new"
            style={{
              fontFamily: C.fontLabel,
              fontSize: 9,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              padding: '10px 24px',
              borderRadius: 4,
              border: 'none',
              background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`,
              color: C.bgCard,
              textDecoration: 'none',
              display: 'inline-block',
              fontWeight: 700,
            }}
          >
            New Rental Contract
          </Link>
        </div>

        {/* ── Primary Stats Grid ───────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
          <Link to="/rentals/list" style={statsCardClickable('active')}
            onMouseEnter={() => setHoveredCard('active')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <StatCard title="Active Contracts" value={stats?.activeContracts || 0} icon="📋" color="blue" />
          </Link>
          <a href="#rented-machines" style={statsCardClickable('rented')}
            onMouseEnter={() => setHoveredCard('rented')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <StatCard title="Total Machines Rented" value={stats?.totalMachinesRented || 0} icon="🖨️" color="green" />
          </a>
          <div title="Monthly revenue from active rental contracts">
            <StatCard title="Monthly Revenue" value={`BD ${(stats?.monthlyRentalRevenue || 0).toFixed(2)}`} icon="💰" color="purple" />
          </div>
          <div
            onClick={() => setShowVisitScheduleModal(true)}
            style={{ cursor: 'pointer', transition: 'opacity 0.2s', opacity: hoveredCard === 'visits' ? 0.8 : 1 }}
            onMouseEnter={() => setHoveredCard('visits')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <StatCard title="Visits This Week" value={visitsLoading ? '...' : (visitsThisWeek || 0)} icon="📅" color="yellow" />
          </div>
        </div>

        {/* ── Secondary Stats Grid ─────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
          {/* Total Contracts */}
          <Link to="/rentals" style={{ textDecoration: 'none' }}
            onMouseEnter={() => setHoveredCard('total')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div style={{
              ...cardStyle,
              padding: 24,
              transition: 'border-color 0.2s, background 0.2s',
              borderColor: hoveredCard === 'total' ? C.borderSubtle : C.borderNormal,
              background: hoveredCard === 'total' ? C.bgElevated : C.bgCard,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.18em', color: C.textMuted, textTransform: 'uppercase', margin: '0 0 6px 0' }}>
                    Total Contracts
                  </p>
                  <p style={{ fontFamily: C.fontHeading, fontSize: 28, fontWeight: 300, color: C.textPrimary, margin: 0 }}>
                    {stats?.totalContracts || 0}
                  </p>
                </div>
                <span style={{ fontSize: 28 }}>📄</span>
              </div>
            </div>
          </Link>

          {/* Oil Usage */}
          <div
            onClick={() => setShowOilUsageModal(true)}
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHoveredCard('oil')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div style={{
              ...cardStyle,
              padding: 24,
              transition: 'border-color 0.2s, background 0.2s',
              borderColor: hoveredCard === 'oil' ? C.borderSubtle : C.borderNormal,
              background: hoveredCard === 'oil' ? C.bgElevated : C.bgCard,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.18em', color: C.textMuted, textTransform: 'uppercase', margin: '0 0 6px 0' }}>
                    Oil Used (This Month)
                  </p>
                  <p style={{ fontFamily: C.fontHeading, fontSize: 28, fontWeight: 300, color: C.textPrimary, margin: 0 }}>
                    {(stats?.oilUsedThisMonth || 0).toFixed(0)} ML
                  </p>
                </div>
                <span style={{ fontSize: 28 }}>💧</span>
              </div>
            </div>
          </div>

          {/* Available Machines */}
          <div
            onClick={() => setShowAvailableMachinesModal(true)}
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHoveredCard('available')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div style={{
              ...cardStyle,
              padding: 24,
              transition: 'border-color 0.2s, background 0.2s',
              borderColor: hoveredCard === 'available' ? C.borderSubtle : C.borderNormal,
              background: hoveredCard === 'available' ? C.bgElevated : C.bgCard,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.18em', color: C.textMuted, textTransform: 'uppercase', margin: '0 0 6px 0' }}>
                    Available Machines
                  </p>
                  <p style={{ fontFamily: C.fontHeading, fontSize: 28, fontWeight: 300, color: C.textPrimary, margin: 0 }}>
                    {stats?.availableMachines || 0}
                  </p>
                </div>
                <span style={{ fontSize: 28 }}>✅</span>
              </div>
            </div>
          </div>

          {/* Expiring Soon */}
          <Link to="/rentals?filter=expiring" style={{ textDecoration: 'none' }}
            onMouseEnter={() => setHoveredCard('expiring')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div style={{
              ...cardStyle,
              padding: 24,
              transition: 'border-color 0.2s, background 0.2s',
              borderColor: hoveredCard === 'expiring' ? C.borderSubtle : C.borderNormal,
              background: hoveredCard === 'expiring' ? C.bgElevated : C.bgCard,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.18em', color: C.textMuted, textTransform: 'uppercase', margin: '0 0 6px 0' }}>
                    Expiring Soon
                  </p>
                  <p style={{ fontFamily: C.fontHeading, fontSize: 28, fontWeight: 300, color: C.textPrimary, margin: 0 }}>
                    {stats?.expiringCount || 0}
                  </p>
                </div>
                <span style={{ fontSize: 28 }}>⚠️</span>
              </div>
            </div>
          </Link>
        </div>

        {/* ── Quick Actions ────────────────────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 28 }}>
          <Link to="/rentals" style={quickCardStyle('qa1') as any}
            onMouseEnter={() => setHoveredCard('qa1')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <h3 style={{ fontFamily: C.fontHeading, fontSize: 20, fontWeight: 400, color: C.textPrimary, margin: '0 0 6px 0' }}>View All Contracts</h3>
            <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: 0 }}>Manage rental contracts and machines</p>
          </Link>

          <Link to="/refills/schedule" style={quickCardStyle('qa2') as any}
            onMouseEnter={() => setHoveredCard('qa2')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <h3 style={{ fontFamily: C.fontHeading, fontSize: 20, fontWeight: 400, color: C.textPrimary, margin: '0 0 6px 0' }}>Schedule Refill</h3>
            <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: 0 }}>Record oil refill visits</p>
          </Link>

          <Link to="/rentals/machines" style={quickCardStyle('qa3') as any}
            onMouseEnter={() => setHoveredCard('qa3')}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <h3 style={{ fontFamily: C.fontHeading, fontSize: 20, fontWeight: 400, color: C.textPrimary, margin: '0 0 6px 0' }}>Machine Status</h3>
            <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: 0 }}>View all machines and their status</p>
          </Link>
        </div>

        {/* ── Machines Needing Refill ──────────────────────────────────────────── */}
        {!refillLoading && machinesNeedingRefill && machinesNeedingRefill.length > 0 && (
          <div id="machines-needing-refill" style={{ ...cardStyle, marginBottom: 24, scrollMarginTop: 24 }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.borderSubtle}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={sectionHeadingStyle}>Machines Needing Refill <span style={{ color: C.textMuted, fontSize: 16 }}>(Next 7 Days)</span></h2>
              <Link to="/refills/schedule" style={goldLinkStyle}>Schedule Refill →</Link>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Customer</th>
                    <th style={thStyle}>Serial Number</th>
                    <th style={thStyle}>Next Refill Date</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {machinesNeedingRefill.slice(0, 5).map((item) => (
                    <tr key={item.machine?.id || Math.random()}>
                      <td style={tdStyle}>{item.contract?.customer?.name || 'N/A'}</td>
                      <td style={tdStyle}>{item.machine?.serialNumber?.serialNumber || 'N/A'}</td>
                      <td style={tdStyle}>
                        {item.schedule?.nextRefillDate ? new Date(item.schedule.nextRefillDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={tdStyle}>
                        <span style={statusBadge(item.isOverdue)}>
                          {item.isOverdue ? 'Overdue' : `Due in ${item.daysUntilDue || 0} days`}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Expiring Contracts ───────────────────────────────────────────────── */}
        {!expiringLoading && expiringContracts && expiringContracts.length > 0 && (
          <div style={{ ...cardStyle, marginBottom: 24 }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.borderSubtle}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={sectionHeadingStyle}>Contracts Expiring Soon <span style={{ color: C.textMuted, fontSize: 16 }}>(Next 30 Days)</span></h2>
              <Link to="/rentals" style={goldLinkStyle}>View All →</Link>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Contract #</th>
                    <th style={thStyle}>Customer</th>
                    <th style={thStyle}>Machines</th>
                    <th style={thStyle}>End Date</th>
                    <th style={thStyle}>Days Until Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  {expiringContracts.slice(0, 5).map((item, index) => (
                    <tr key={item.contract?.id || index}>
                      <td style={tdStyle}>
                        <Link to={`/rentals/${item.contract?.id || ''}`} style={{ color: C.gold, textDecoration: 'none', fontFamily: C.fontBody }}>
                          {item.contract?.contractNumber || 'N/A'}
                        </Link>
                      </td>
                      <td style={tdStyle}>{item.customer?.name || 'N/A'}</td>
                      <td style={tdStyle}>{item.machineCount || 0}</td>
                      <td style={tdStyle}>
                        {item.contract?.endDate ? new Date(item.contract.endDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={tdStyle}>
                        <span style={contractStatusBadge()}>{item.daysUntilExpiry || 0} days</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Rented Machines Summary ──────────────────────────────────────────── */}
        {!rentedLoading && rentedMachines && rentedMachines.length > 0 && (
          <div id="rented-machines" style={{ ...cardStyle, marginBottom: 24, scrollMarginTop: 24 }}>
            <div style={{ padding: '18px 24px', borderBottom: `1px solid ${C.borderSubtle}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={sectionHeadingStyle}>Currently Rented Machines</h2>
              <Link to="/rentals/machines" style={goldLinkStyle}>View All →</Link>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Serial Number</th>
                    <th style={thStyle}>Customer</th>
                    <th style={thStyle}>Contract #</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rentedMachines.slice(0, 10).map((item, index) => (
                    <tr key={item.machine?.id || item.serialNumber?.id || index}>
                      <td style={{ ...tdStyle, color: C.textPrimary }}>{item.serialNumber?.serialNumber || 'N/A'}</td>
                      <td style={tdStyle}>{item.customer?.name || 'N/A'}</td>
                      <td style={tdStyle}>
                        <Link to={`/rentals/${item.contract?.id || ''}`} style={{ color: C.gold, textDecoration: 'none', fontFamily: C.fontBody }}>
                          {item.contract?.contractNumber || 'N/A'}
                        </Link>
                      </td>
                      <td style={tdStyle}>
                        <span style={rentedBadge()}>{item.machine?.status || 'RENTED'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Available Machines Modal ─────────────────────────────────────────── */}
        {showAvailableMachinesModal && (
          <div style={modalOverlay}>
            <div style={{ ...modalBox, maxWidth: 1100 }}>
              <div style={modalHeader}>
                <div>
                  <h2 style={{ fontFamily: C.fontHeading, fontSize: 28, fontWeight: 300, color: C.textPrimary, margin: '0 0 4px 0', letterSpacing: '0.03em' }}>
                    Available Machines for Rental
                  </h2>
                  <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: 0 }}>
                    All SERIALIZED products with IN_STOCK status
                  </p>
                </div>
                <button onClick={() => setShowAvailableMachinesModal(false)} style={closeBtn}>×</button>
              </div>

              <div style={modalBody}>
                {availableMachinesLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px 0' }}>
                    <div style={{ width: 40, height: 40, border: `2px solid ${C.borderSubtle}`, borderTop: `2px solid ${C.gold}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                  </div>
                ) : !availableMachines || availableMachines.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 0' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>📦</div>
                    <h3 style={{ fontFamily: C.fontHeading, fontSize: 22, fontWeight: 300, color: C.textPrimary, margin: '0 0 8px 0' }}>No Available Machines</h3>
                    <p style={{ fontFamily: C.fontBody, fontSize: 13, color: C.textMuted, margin: 0 }}>All machines are currently rented or not in stock.</p>
                  </div>
                ) : (
                  <>
                    <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: '0 0 16px 0' }}>
                      Showing {availableMachines.length} available machine{availableMachines.length !== 1 ? 's' : ''}
                    </p>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Serial Number</th>
                            <th style={thStyle}>Product Name</th>
                            <th style={thStyle}>SKU</th>
                            <th style={thStyle}>Warehouse</th>
                            <th style={thStyle}>Condition</th>
                            <th style={thStyle}>Location</th>
                            <th style={thStyle}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {availableMachines.map((machine) => (
                            <tr key={machine.id}>
                              <td style={{ ...tdStyle, color: C.gold }}>{machine.serialNumber}</td>
                              <td style={{ ...tdStyle, color: C.textPrimary }}>{machine.product?.name || 'N/A'}</td>
                              <td style={tdStyle}>{machine.product?.sku || 'N/A'}</td>
                              <td style={tdStyle}>{machine.warehouse?.name || 'N/A'}</td>
                              <td style={tdStyle}>
                                <span style={conditionBadge(machine.condition)}>{machine.condition}</span>
                              </td>
                              <td style={tdStyle}>{machine.location || '-'}</td>
                              <td style={tdStyle}>
                                <span style={rentedBadge()}>{machine.status}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>

              <div style={modalFooter}>
                <button
                  onClick={() => setShowAvailableMachinesModal(false)}
                  onMouseEnter={() => setHoveredBtn('close-avail')}
                  onMouseLeave={() => setHoveredBtn(null)}
                  style={primaryBtn('close-avail')}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Oil Usage Modal ──────────────────────────────────────────────────── */}
        {showOilUsageModal && (
          <div style={modalOverlay}>
            <div style={{ ...modalBox, maxWidth: 1200 }}>
              <div style={modalHeader}>
                <div>
                  <h2 style={{ fontFamily: C.fontHeading, fontSize: 28, fontWeight: 300, color: C.textPrimary, margin: '0 0 4px 0', letterSpacing: '0.03em' }}>
                    Oil Usage This Month
                  </h2>
                  <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: 0 }}>
                    All refill visit transactions for {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <button onClick={() => setShowOilUsageModal(false)} style={closeBtn}>×</button>
              </div>

              <div style={modalBody}>
                {oilUsageLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '48px 0' }}>
                    <div style={{ width: 40, height: 40, border: `2px solid ${C.borderSubtle}`, borderTop: `2px solid ${C.gold}`, borderRadius: '50%' }} />
                  </div>
                ) : !oilUsageData || oilUsageData.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '48px 0' }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>💧</div>
                    <h3 style={{ fontFamily: C.fontHeading, fontSize: 22, fontWeight: 300, color: C.textPrimary, margin: '0 0 8px 0' }}>No Oil Usage This Month</h3>
                    <p style={{ fontFamily: C.fontBody, fontSize: 13, color: C.textMuted, margin: 0 }}>No refill visits have been recorded this month yet.</p>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: 0 }}>
                        Showing {oilUsageData.length} refill visit{oilUsageData.length !== 1 ? 's' : ''}
                      </p>
                      <span style={{ fontFamily: C.fontHeading, fontSize: 20, fontWeight: 300, color: C.goldLight }}>
                        Total: {oilUsageData.reduce((sum: number, visit: any) => sum + (visit.totalOilUsedML || 0), 0).toFixed(0)} ML
                      </span>
                    </div>
                    <div style={{ overflowX: 'auto', marginBottom: 28 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Visit #</th>
                            <th style={thStyle}>Date</th>
                            <th style={thStyle}>Customer</th>
                            <th style={thStyle}>Contract #</th>
                            <th style={thStyle}>Machines Refilled</th>
                            <th style={thStyle}>Oil Used (ML)</th>
                            <th style={thStyle}>Employee</th>
                          </tr>
                        </thead>
                        <tbody>
                          {oilUsageData.map((visit: any) => (
                            <tr key={visit.id}>
                              <td style={{ ...tdStyle, color: C.gold }}>{visit.visitNumber}</td>
                              <td style={tdStyle}>{new Date(visit.visitDate).toLocaleDateString()}</td>
                              <td style={{ ...tdStyle, color: C.textPrimary }}>{visit.contract?.customer?.name || 'N/A'}</td>
                              <td style={tdStyle}>
                                <Link to={`/rentals/${visit.contractId}`} style={{ color: C.gold, textDecoration: 'none', fontFamily: C.fontBody }}>
                                  {visit.contract?.contractNumber || 'N/A'}
                                </Link>
                              </td>
                              <td style={tdStyle}>{visit.details?.length || 0} machine{visit.details?.length !== 1 ? 's' : ''}</td>
                              <td style={tdStyle}>
                                <span style={mlBadge()}>{(visit.totalOilUsedML || 0).toFixed(0)} ML</span>
                              </td>
                              <td style={tdStyle}>
                                {visit.employee ? `${visit.employee.firstName} ${visit.employee.lastName}` : 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Detailed Breakdown */}
                    <h3 style={{ fontFamily: C.fontHeading, fontSize: 20, fontWeight: 300, color: C.textPrimary, margin: '0 0 16px 0' }}>
                      Detailed Breakdown by Visit
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {oilUsageData.map((visit: any) => (
                        <div key={visit.id} style={{ background: C.bgElevated, border: `1px solid ${C.borderSubtle}`, borderRadius: 6, padding: 20 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                            <div>
                              <h4 style={{ fontFamily: C.fontHeading, fontSize: 18, fontWeight: 400, color: C.textPrimary, margin: '0 0 4px 0' }}>
                                {visit.visitNumber} — {visit.contract?.customer?.name}
                              </h4>
                              <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textMuted, margin: 0 }}>
                                {new Date(visit.visitDate).toLocaleDateString()} at {new Date(visit.visitDate).toLocaleTimeString()}
                              </p>
                            </div>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', padding: '3px 12px', borderRadius: 20,
                              fontSize: 11, fontFamily: C.fontLabel, letterSpacing: '0.14em', fontWeight: 600,
                              background: 'rgba(140,90,201,0.15)', color: '#c07ae8', border: '1px solid rgba(140,90,201,0.3)',
                            }}>
                              {(visit.totalOilUsedML || 0).toFixed(0)} ML Total
                            </span>
                          </div>

                          {visit.details && visit.details.length > 0 && (
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr>
                                    <th style={{ ...thStyle, background: C.bgCard }}>Machine Serial</th>
                                    <th style={{ ...thStyle, background: C.bgCard }}>Oil Product</th>
                                    <th style={{ ...thStyle, background: C.bgCard }}>Amount (ML)</th>
                                    <th style={{ ...thStyle, background: C.bgCard }}>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visit.details.map((detail: any, idx: number) => (
                                    <tr key={idx}>
                                      <td style={{ ...tdStyle, color: C.textPrimary }}>{detail.machine?.serialNumber?.serialNumber || 'N/A'}</td>
                                      <td style={tdStyle}>{detail.oilProduct?.name || 'N/A'}</td>
                                      <td style={{ ...tdStyle, color: '#7ab0e8', fontWeight: 600 }}>{(detail.amountML || 0).toFixed(0)} ML</td>
                                      <td style={tdStyle}>{detail.notes || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {visit.notes && (
                            <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(239,232,213,0.6)', border: `1px solid ${C.borderNormal}`, borderRadius: 4 }}>
                              <p style={{ fontFamily: C.fontBody, fontSize: 12, color: C.textSecondary, margin: 0 }}>
                                <span style={{ fontFamily: C.fontLabel, fontSize: 8, letterSpacing: '0.14em', color: C.textMuted, textTransform: 'uppercase', marginRight: 8 }}>Visit Notes</span>
                                {visit.notes}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div style={modalFooter}>
                <button
                  onClick={() => setShowOilUsageModal(false)}
                  onMouseEnter={() => setHoveredBtn('close-oil')}
                  onMouseLeave={() => setHoveredBtn(null)}
                  style={primaryBtn('close-oil')}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Refill Schedule Modal */}
        <RefillScheduleModal
          isOpen={showRefillScheduleModal}
          onClose={() => setShowRefillScheduleModal(false)}
        />

        {/* Visit Schedule Modal */}
        <VisitScheduleModal
          isOpen={showVisitScheduleModal}
          onClose={() => setShowVisitScheduleModal(false)}
        />
      </div>
    </div>
  );
};
